// frontend/src/infrastructure/offline/AppDatabase.ts
//
// Lokalna baza IndexedDB oparta o Dexie — fundament trybu offline-first.
// Warstwa infrastruktury: żaden inny moduł nie powinien odwoływać się
// bezpośrednio do `indexedDB`.

import Dexie, { type Table } from 'dexie'

/**
 * Pojedynczy wpis kolejki synchronizacji.
 * Reprezentuje operację wykonaną w trybie offline, która musi zostać
 * odtworzona na serwerze po odzyskaniu połączenia (kolejność FIFO).
 */
export interface SyncQueueItem {
  /** Klucz główny (autoinkrement Dexie). Brak przy zapisie nowego wpisu. */
  id?: number
  /** Typ encji, której dotyczy operacja, np. `'incident'`, `'employee'`. */
  entityType: string
  /** Rodzaj operacji do wykonania na serwerze, np. `'create'`, `'update'`, `'delete'`. */
  action: string
  /**
   * Ładunek operacji. Celowo `unknown` zamiast `any` — wymusza zawężenie
   * typu w miejscu konsumpcji (reguła: kod silnie typowany).
   */
  payload: unknown
  /** Znacznik czasu utworzenia wpisu (`Date.now()`, ms) — klucz sortowania. */
  createdAt: number
}

/** Dane nowego wpisu — bez `id`, które nadaje Dexie przy zapisie. */
export type NewSyncQueueItem = Omit<SyncQueueItem, 'id'>

/** Nazwa bazy w IndexedDB. */
export const DB_NAME = 'BlackAppOfflineDB'

export class AppDatabase extends Dexie {
  // `declare` jest wymagane przy `useDefineForClassFields: true`
  // (patrz tsconfig.app.json) — bez tego pole nadpisałoby właściwość
  // ustawianą przez konstruktor Dexie wartością `undefined`.
  declare readonly syncQueue: Table<SyncQueueItem, number>

  constructor() {
    super(DB_NAME)

    this.version(1).stores({
      // `++id` — klucz główny autoinkrement.
      // Pozostałe pola indeksowane pod zapytania (`createdAt` => sortowanie FIFO).
      syncQueue: '++id, entityType, action, createdAt',
    })
  }
}

/** Współdzielona instancja bazy dla całej aplikacji. */
export const db = new AppDatabase()
